import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { createPortal } from "react-dom";
import { FileText, Mic, Square, Send, X, Loader2, BadgeCheck, Play, Trash2 } from "lucide-react";
import MonthlyDossier from "../report/MonthlyDossier";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOLD = "#D4AF37", RED = "#C41E24";
const MAX_SEC = 60;
const thisMonth = () => new Date().toISOString().slice(0, 7);
const monthLabel = (m) => new Date(`${m}-01T00:00:00`).toLocaleDateString("en-US", { month: "long", year: "numeric" });

// 60-second "Monthly Audio Verdict" recorder.
const VerdictRecorder = ({ blob, setBlob, existing }) => {
  const recRef = useRef(null), chunks = useRef([]), timer = useRef(null);
  const [rec, setRec] = useState(false);
  const [sec, setSec] = useState(0);
  const [url, setUrl] = useState("");
  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);
  const stop = () => { if (recRef.current && recRef.current.state !== "inactive") recRef.current.stop(); clearInterval(timer.current); setRec(false); };
  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunks.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunks.current.push(e.data); };
      mr.onstop = () => { const b = new Blob(chunks.current, { type: "audio/webm" }); setBlob(b); setUrl(URL.createObjectURL(b)); stream.getTracks().forEach((t) => t.stop()); };
      recRef.current = mr; mr.start(); setRec(true); setSec(0);
      timer.current = setInterval(() => setSec((s) => { if (s + 1 >= MAX_SEC) { stop(); return MAX_SEC; } return s + 1; }), 1000);
    } catch (e) { toast.error("Microphone access denied."); }
  };
  return (
    <div data-testid="verdict-recorder" className="rounded-lg border p-3 flex items-center gap-3 flex-wrap" style={{ borderColor: `${GOLD}44`, background: `${GOLD}0a` }}>
      <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest" style={{ color: GOLD }}><Mic size={12} /> Monthly Audio Verdict · 60s</div>
      {rec ? (
        <button data-testid="verdict-stop" onClick={stop} className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider px-3 py-2 rounded-sm text-white" style={{ background: RED }}><Square size={12} /> Stop · 0:{String(sec).padStart(2, "0")}</button>
      ) : (
        <button data-testid="verdict-record" onClick={start} className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider px-3 py-2 rounded-sm border" style={{ borderColor: GOLD, color: GOLD }}><span className="w-2.5 h-2.5 rounded-full" style={{ background: RED }} /> {blob ? "Re-record" : "Record Verdict"}</button>
      )}
      {url && <audio data-testid="verdict-preview" controls src={url} className="h-9 max-w-[220px]" />}
      {blob && !rec && <button data-testid="verdict-clear" onClick={() => { setBlob(null); setUrl(""); }} className="text-white/40 hover:text-white" aria-label="Discard recording"><Trash2 size={14} /></button>}
      {!blob && existing && <span className="text-[10px] font-mono uppercase tracking-wider text-white/45 inline-flex items-center gap-1"><Play size={10} /> Keeping the released verdict</span>}
    </div>
  );
};

// Coach: preview + note + audio verdict -> Release Report to the player's Hub.
export default function DossierRelease({ token, player }) {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [month, setMonth] = useState(thisMonth());
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [note, setNote] = useState("");
  const [blob, setBlob] = useState(null);
  const [releasing, setReleasing] = useState(false);

  const preview = async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/coach/players/${player.id}/dossier/${month}`, auth);
      setData(d); setNote(d.released?.coach_note || ""); setBlob(null); setOpen(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't build the dossier."); }
    setLoading(false);
  };
  const release = async () => {
    setReleasing(true);
    try {
      const fd = new FormData();
      fd.append("coach_note", note);
      if (blob) fd.append("voice", blob, "verdict.webm");
      const { data: d } = await axios.post(`${API}/coach/players/${player.id}/dossier/${month}/release`, fd, auth);
      setData((x) => ({ ...x, released: d }));
      toast.success(`${monthLabel(month)} dossier released`, { description: `Published to ${player.name?.split(" ")[0] || "the player"}'s Hub${d.has_voice ? " with your audio verdict" : ""}.` });
    } catch (e) { toast.error(e?.response?.data?.detail || "Release failed."); }
    setReleasing(false);
  };
  const months = Array.from({ length: 6 }, (_, i) => { const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - i); return d.toISOString().slice(0, 7); });

  return (
    <div data-testid="dossier-release-panel" className="mt-4 rounded-lg border p-4" style={{ borderColor: `${GOLD}44`, background: "linear-gradient(160deg, rgba(212,175,55,0.06), rgba(10,10,10,0.6))" }}>
      <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest mb-3" style={{ color: GOLD }}><FileText size={12} /> Elite Scouting Dossier · Monthly Performance Report</div>
      <div className="flex flex-wrap items-center gap-2">
        <select data-testid="dossier-month" value={month} onChange={(e) => setMonth(e.target.value)} className="bg-[#141414] border border-white/10 rounded-md px-3 h-10 text-white text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37]">
          {months.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
        </select>
        <button data-testid="dossier-preview-btn" onClick={preview} disabled={loading} className="flex items-center gap-2 h-10 px-4 rounded-sm text-black text-xs font-bold uppercase tracking-wider disabled:opacity-60" style={{ background: GOLD }}>{loading ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />} Preview &amp; Release</button>
      </div>

      {open && data && createPortal(
        <div data-testid="dossier-modal" className="fixed inset-0 z-[80] bg-black/90 overflow-y-auto" onClick={() => setOpen(false)}>
          <div className="min-h-full flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 z-10 bg-[#0a0a0a]/95 backdrop-blur border-b px-4 sm:px-6 py-3 flex flex-col gap-3" style={{ borderColor: `${GOLD}33` }}>
              <div className="flex items-center justify-between gap-3">
                <div className="font-headings text-lg uppercase tracking-wide truncate">{player.name} · {monthLabel(month)} {data.released && <span data-testid="dossier-released-tag" className="ml-2 inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-wider align-middle" style={{ color: GOLD }}><BadgeCheck size={11} /> Released {new Date(data.released.released_at).toLocaleDateString()}</span>}</div>
                <button data-testid="dossier-close" onClick={() => setOpen(false)} className="p-2 text-white/50 hover:text-white" aria-label="Close"><X size={20} /></button>
              </div>
              <textarea data-testid="dossier-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={1200} placeholder="Your monthly note to the family — what changed, what's next…" className="w-full bg-[#141414] border border-white/10 rounded-md p-3 text-white text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37]" />
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex-1"><VerdictRecorder blob={blob} setBlob={setBlob} existing={data.released?.has_voice} /></div>
                <button data-testid="dossier-release-btn" onClick={release} disabled={releasing} className="h-12 px-6 rounded-sm text-white font-headings text-lg uppercase tracking-wide flex items-center justify-center gap-2 disabled:opacity-60" style={{ background: RED, boxShadow: `0 10px 30px ${RED}55` }}>{releasing ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />} {data.released ? "Re-release Report" : "Release Report"}</button>
              </div>
            </div>
            <MonthlyDossier r={{ ...data.report, coach_note: note, released_at: data.released?.released_at, dossier_no: data.released?.dossier_no || data.report.dossier_no || "PREVIEW" }} />
          </div>
        </div>, document.body)}
    </div>
  );
}
